"use client";

import { useState, useRef, useLayoutEffect, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface Props {
  content: ReactNode;
  children: ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  maxWidth?: number;
  className?: string;
  disabled?: boolean;
}

/**
 * Shared FrenFlow tooltip — single visual/behavioral source of truth.
 * Rendered via React portal to `document.body` so it escapes
 * `overflow:hidden` parents (cards, modals, tables).
 */
export default function Tooltip({
  content,
  children,
  position = "top",
  maxWidth = 260,
  className = "",
  disabled = false,
}: Props) {
  const [show, setShow] = useState(false);
  // Distinguishes a click/tap open (sticky, auto-dismisses on a timer
  // or next tap) from a hover open (dismisses on mouseleave). Without
  // this, tapping on mobile shows the tooltip and the immediate
  // synthetic mouseleave that mobile browsers fire kills it before
  // the user can read anything.
  const [clickLocked, setClickLocked] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; shiftX?: number } | null>(null);
  // One-shot guard: the viewport-shift effect must run only ONCE per show
  // cycle, otherwise it can re-measure and re-set coords on every render
  // (the previous implementation listed `coords` as a dep AND called
  // setCoords({...coords, shiftX}) inside, producing React #185 loops when
  // shiftX oscillated near the viewport edge).
  const shiftAppliedRef = useRef(false);

  useLayoutEffect(() => {
    if (!show || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    let top = 0;
    let left = 0;
    switch (position) {
      case "top":    top = rect.top - gap;  left = rect.left + rect.width / 2; break;
      case "bottom": top = rect.bottom + gap; left = rect.left + rect.width / 2; break;
      case "left":   top = rect.top + rect.height / 2; left = rect.left - gap; break;
      case "right":  top = rect.top + rect.height / 2; left = rect.right + gap; break;
    }
    shiftAppliedRef.current = false;
    setCoords({ top, left });
  }, [show, position]);

  // After the tooltip renders, measure its real width and shift it back into
  // the viewport if it's clipping against an edge. Only the horizontal axis
  // matters for top/bottom-positioned tooltips (the most common case).
  // Runs once per show via shiftAppliedRef.
  useLayoutEffect(() => {
    if (!show || !coords || !tooltipRef.current) return;
    if (shiftAppliedRef.current) return;
    if (position !== "top" && position !== "bottom") return;
    const rect = tooltipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const pad = 8;
    let shiftX = 0;
    if (rect.right > vw - pad) shiftX = vw - pad - rect.right;
    else if (rect.left < pad) shiftX = pad - rect.left;
    shiftAppliedRef.current = true;
    if (shiftX !== 0) {
      setCoords(c => (c ? { ...c, shiftX } : c));
    }
  }, [show, coords, position]);

  // Reset the guard when the tooltip hides, so the next show recomputes shift.
  useEffect(() => {
    if (!show) shiftAppliedRef.current = false;
  }, [show]);

  // Auto-dismiss tap-opened tooltips after 3s — hover-opened ones
  // close on their own when the cursor leaves.
  useEffect(() => {
    if (!show || !clickLocked) return;
    const id = setTimeout(() => {
      setShow(false);
      setClickLocked(false);
    }, 3000);
    return () => clearTimeout(id);
  }, [show, clickLocked]);

  if (disabled || content == null || content === "") {
    return <>{children}</>;
  }

  const transform = {
    top:    "translate(-50%, -100%)",
    bottom: "translate(-50%, 0)",
    left:   "translate(-100%, -50%)",
    right:  "translate(0, -50%)",
  }[position];

  return (
    <>
      <span
        ref={triggerRef}
        className={`relative inline-flex ${className}`}
        onMouseEnter={() => { if (!clickLocked) setShow(true); }}
        onMouseLeave={() => { if (!clickLocked) setShow(false); }}
        onFocus={() => { if (!clickLocked) setShow(true); }}
        onBlur={() => { if (!clickLocked) setShow(false); }}
        onClick={(e) => {
          // Stop propagation so wrapping the tooltip around an
          // existing clickable (link/button) doesn't accidentally
          // toggle the surface action when the user only wanted to
          // see the tooltip body. Callers can still use a separate
          // anchor for the action surface if both are needed.
          e.stopPropagation();
          setClickLocked(prev => {
            const next = !prev;
            setShow(next);
            return next;
          });
        }}
      >
        {children}
      </span>
      {show && coords && typeof document !== "undefined" && createPortal(
        <div
          ref={tooltipRef}
          role="tooltip"
          // `break-words` so a very long Spanish word can't push past
          // maxWidth. Removed the previous `minWidth: max-content` —
          // CSS specifies min-width wins over max-width, so it forced
          // single-line layout that overflowed the viewport on long
          // tooltip text (UMA stat tips were the visible regression).
          className="fixed block px-3 py-2 bg-white dark:bg-[#0f0f12] border border-gray-200 dark:border-[#25252B] rounded-[5px] text-[11px] leading-snug text-gray-600 dark:text-gray-300 font-normal normal-case whitespace-pre-line break-words text-left shadow-[0_4px_20px_rgba(0,0,0,0.15)] dark:shadow-[0_4px_20px_rgba(0,0,0,0.5)] z-[9999] pointer-events-none"
          style={{
            top: coords.top,
            left: coords.left + (coords.shiftX || 0),
            transform,
            maxWidth,
          }}
        >
          {content}
        </div>,
        document.body
      )}
    </>
  );
}
